// src/core/redux/userpoint.slice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { userPointService } from "../services/userpoint.service";

// Thunks
export const fetchUserPoints = createAsyncThunk(
  "userPoints/fetchAll",
  async (_, thunkAPI) => {
    try {
      return await userPointService.getUserPoints();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch user points");
    }
  }
);

export const fetchUserPointsByEmployee = createAsyncThunk(
  "userPoints/fetchByEmployee",
  async (employeeId, thunkAPI) => {
    try {
      return await userPointService.getUserPointsByEmployee(employeeId);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch user points by employee");
    }
  }
);

export const fetchUserPointById = createAsyncThunk(
  "userPoints/fetchById",
  async (id, thunkAPI) => {
    try {
      return await userPointService.getUserPointById(id);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch user point");
    }
  }
);

export const addUserPoint = createAsyncThunk(
  "userPoints/add",
  async (userPoint, thunkAPI) => {
    try {
      return await userPointService.createUserPoint(userPoint);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to create user point");
    }
  }
);

export const editUserPoint = createAsyncThunk(
  "userPoints/edit",
  async ({ id, userPoint }, thunkAPI) => {
    try {
      await userPointService.updateUserPoint(id, userPoint);
      return { id, ...userPoint };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update user point");
    }
  }
);

export const softRemoveUserPoint = createAsyncThunk(
  "userPoints/softRemove",
  async (id, thunkAPI) => {
    try {
      await userPointService.softDeleteUserPoint(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to soft delete user point");
    }
  }
);

export const hardRemoveUserPoint = createAsyncThunk(
  "userPoints/hardRemove",
  async (id, thunkAPI) => {
    try {
      await userPointService.hardDeleteUserPoint(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to hard delete user point");
    }
  }
);

export const addUserPointsBulk = createAsyncThunk(
  "userPoints/addBulk",
  async (userPoints, thunkAPI) => {
    try {
      // userPoints doit être un tableau de UserPointsCreateDto
      return await userPointService.createBulkUserPoint(userPoints);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to create user points in bulk");
    }
  }
);


// Slice
const userPointSlice = createSlice({
  name: "userPoints",
  initialState: {
    items: [],
    selected: null,
    loading: false,
    error: null,
    showModal: false,
    showDeleteModal: false,
  },
  reducers: {
    openUserPointModal: (state) => {
      state.showModal = true;
    },
    closeUserPointModal: (state) => {
      state.showModal = false;
    },
    openUserPointDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeUserPointDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch all
      .addCase(fetchUserPoints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPoints.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchUserPoints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetch by employee
      .addCase(fetchUserPointsByEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPointsByEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchUserPointsByEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetch by id
      .addCase(fetchUserPointById.fulfilled, (state, action) => {
        state.selected = action.payload;
      })

      // add
      .addCase(addUserPoint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addUserPoint.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.showModal = false;
      })
      .addCase(addUserPoint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // edit
      .addCase(editUserPoint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editUserPoint.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.items.findIndex(p => p.userPointId === action.payload.id);
        if (idx !== -1) {
          state.items[idx] = { ...state.items[idx], ...action.payload };
        }
        state.showModal = false;
      })
      .addCase(editUserPoint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // soft delete
      .addCase(softRemoveUserPoint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(softRemoveUserPoint.fulfilled, (state, action) => {
        state.loading = false;
        // selon ton modèle : soft delete => par ex. isDeleted = true, sinon filtrer
        state.items = state.items.filter(p => p.userPointId !== action.payload);
        state.showDeleteModal = false;
      })
      .addCase(softRemoveUserPoint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // hard delete
      .addCase(hardRemoveUserPoint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(hardRemoveUserPoint.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(p => p.userPointId !== action.payload);
        state.showDeleteModal = false;
      })
      .addCase(hardRemoveUserPoint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
            // bulk add
      .addCase(addUserPointsBulk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addUserPointsBulk.fulfilled, (state, action) => {
        state.loading = false;
        // l’API renvoie { Items, TotalCount }
        const items = action.payload.items || action.payload.Items || [];
        state.items = [...state.items, ...items];
      })
      .addCase(addUserPointsBulk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      
      ;
  },
});

export const {
  openUserPointModal,
  closeUserPointModal,
  openUserPointDeleteModal,
  closeUserPointDeleteModal,
} = userPointSlice.actions;

export default userPointSlice.reducer;
